import React, { useState, useEffect, useRef } from 'react';
import { MUTED_PALETTE } from './constants';
import { playSoftClick } from './audioUtils';

// Shape component defined outside to avoid recreation
const SortingShape = ({ item }) => {
  const baseClass = 'w-14 h-14 transition-all duration-200 cursor-grab active:cursor-grabbing hover:scale-110';
  if (item.shape === 'circle') return <div className={`${baseClass} rounded-full`} style={{ backgroundColor: item.color }} />;
  if (item.shape === 'square') return <div className={`${baseClass} rounded-md`} style={{ backgroundColor: item.color }} />;
  return <div className={baseClass} style={{ width: 0, height: 0, borderLeft: '28px solid transparent', borderRight: '28px solid transparent', borderBottom: `48px solid ${item.color}` }} />;
};

const VisualSorting = ({ audioCtxRef, soundEnabled }) => {
  const COLORS = {
    sage: '#9CAF88',
    dustyRose: '#C9A9A6', 
    lavender: '#B4A7C7',
    seafoam: '#8FBCBB',
  };
  
  const [items, setItems] = useState(() => {
    const shapes = ['circle', 'square', 'triangle'];
    const colors = [COLORS.sage, COLORS.dustyRose, COLORS.lavender, COLORS.seafoam];
    const newItems = [];
    for (let i = 0; i < 16; i++) {
      newItems.push({
        id: i,
        shape: shapes[Math.floor(Math.random() * shapes.length)],
        color: colors[Math.floor(Math.random() * colors.length)],
        sorted: false,
      });
    }
    return newItems.sort(() => Math.random() - 0.5);
  });
  const [sorted, setSorted] = useState(0);
  const [draggedItem, setDraggedItem] = useState(null);
  
  const handleReset = () => {
    const shapes = ['circle', 'square', 'triangle'];
    const colors = [COLORS.sage, COLORS.dustyRose, COLORS.lavender, COLORS.seafoam];
    const newItems = [];
    for (let i = 0; i < 16; i++) {
      newItems.push({
        id: i,
        shape: shapes[Math.floor(Math.random() * shapes.length)],
        color: colors[Math.floor(Math.random() * colors.length)],
        sorted: false,
      });
    }
    setItems(newItems.sort(() => Math.random() - 0.5));
    setSorted(0);
  };
  
  const handleDrop = (targetColor) => {
    if (!draggedItem) return;
    
    if (draggedItem.color === targetColor) {
      setItems(items.map(item => item.id === draggedItem.id ? { ...item, sorted: true } : item));
      setSorted(s => s + 1);
      const audioCtx = audioCtxRef?.current;
      if (soundEnabled && audioCtx) playSoftClick(audioCtx);
    }
    setDraggedItem(null);
  };
  
  const isComplete = items.length > 0 && items.every(i => i.sorted);
  
  return (
    <div className="flex flex-col items-center justify-center h-full gap-8">
      <div className="flex items-center gap-4">
        <p className="text-white/70 text-xl">Drag shapes to matching color bins • {sorted}/16 sorted</p>
        {isComplete && (
          <button
            onClick={handleReset}
            className="px-4 py-2 rounded-full bg-white/20 hover:bg-white/30 text-white text-sm transition-all"
          >
            🔄 Play Again
          </button>
        )}
      </div>
      
      <div className="flex flex-wrap gap-4 justify-center p-8 bg-white/10 rounded-2xl shadow-xl" style={{ width: '85vw', maxWidth: '700px', minHeight: '180px' }}>
        {items.filter(i => !i.sorted).map(item => (
          <div key={item.id} draggable onDragStart={() => setDraggedItem(item)} onDragEnd={() => setDraggedItem(null)}>
            <SortingShape item={item} />
          </div>
        ))}
        {isComplete && <p className="text-white/50 py-6 text-lg">✨ All sorted! Beautiful work.</p>}
      </div>
      
      <div className="flex gap-6">
        {[COLORS.sage, COLORS.dustyRose, COLORS.lavender, COLORS.seafoam].map(color => (
          <div
            key={color}
            className="w-28 h-28 rounded-xl border-3 border-dashed flex items-center justify-center transition-all shadow-lg"
            style={{ borderColor: color, borderWidth: '3px', backgroundColor: draggedItem?.color === color ? `${color}40` : 'rgba(255,255,255,0.05)' }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(color)}
          >
            <div className="w-10 h-10 rounded-full" style={{ backgroundColor: color }} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default VisualSorting;
